import { useEffect, useMemo, useState, useRef } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Table,
  TableContainer,
  TableBody,
  TableRow,
  TableCell,
  TableSortLabel,
  TextField,
  MenuItem,
  CircularProgress,
  Box,
  Snackbar,
  Alert,
  IconButton,
  Typography,
  Link,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import useMediaQuery from "@mui/material/useMediaQuery";
import PropTypes from "prop-types";
import axios from "axios";
import {
  broilerSettingsUrl,
  allFarmsUrl,
  broilerDataByFarmUrl,
  broilersByFarmAndFacilityUrl,
  farmStrainByFarmUrl,
  updateBroilerSettingsUrl,
  feedScheduleUrl,
  hatcheryDataUrl,
  updateFeedTotalUrl,
  updateFeedWaterUrl,
  removeFeedUrl,
} from "api/endpoints";
import {
  readOfflineRecords,
  upsertOfflineRecord,
  removeOfflineRecord,
  replaceOfflineRecords,
  clearOfflineRecords,
} from "utils/offlineRecords";
import Inputs from "./inputs";
import ProductionActivities from "./productionactivities";

const toWeightDisplay = (val) => {
  if (val === null || val === undefined) return "";
  const str = String(val).trim();
  if (!str) return "";
  const num = Number(str);
  if (Number.isNaN(num)) return str;
  return num.toFixed(2);
};

const toIntDisplay = (val) => {
  if (val === null || val === undefined) return "";
  const str = String(val).trim();
  if (!str) return "";
  const num = Number(str);
  if (Number.isNaN(num)) return str;
  return String(Math.trunc(num));
};

const sumWeightParts = (...parts) => {
  let total = 0;
  let hasValue = false;
  parts.forEach((part) => {
    const num = Number(part);
    if (Number.isFinite(num)) {
      total += num;
      hasValue = true;
    }
  });
  return hasValue ? total : null;
};

export default function SetupCycle({
  facilityId,
  cycleId,
  farmId,
  embeddedFeed = false,
  onFeedSaved,
}) {
  const isSmallScreen = useMediaQuery("(max-width:600px)");
  const inputsRef = useRef(null);
  const [openDialog, setOpenDialog] = useState(embeddedFeed ? "feed" : null);
  const [placements, setPlacements] = useState([]);
  const [placementsLoading, setPlacementsLoading] = useState(false);
  const [placementsError, setPlacementsError] = useState("");
  const [selectedHatcheryDataId, setSelectedHatcheryDataId] = useState(null);
  const [selectedPlacementInvoice, setSelectedPlacementInvoice] = useState(null);
  const [placementRefreshCount, setPlacementRefreshCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [placementDate, setPlacementDate] = useState("");
  const [finalCatchDate, setFinalCatchDate] = useState("");
  const [managementPlan, setManagementPlan] = useState("");
  const [managementPlanOptions, setManagementPlanOptions] = useState([]);
  const [managementPlanMap, setManagementPlanMap] = useState({});
  const [settingId, setSettingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState("Production Cycle saved successfully");
  const [reloadKey, setReloadKey] = useState(0);
  const resetProductionCycleFields = () => {
    setPlacementDate("");
    setFinalCatchDate("");
    setSettingId(null);
    setManagementPlan("");
  };

  const [feedRows, setFeedRows] = useState([]);
  const [feedStartOrder, setFeedStartOrder] = useState("desc");
  const [feedLoading, setFeedLoading] = useState(false);
  const [feedError, setFeedError] = useState(null);
  const [feedStageOptions, setFeedStageOptions] = useState([]);
  const [feedStageMap, setFeedStageMap] = useState({});
  const [feedSupplierOptions, setFeedSupplierOptions] = useState([]);
  const [feedSupplierMap, setFeedSupplierMap] = useState({});
  const [feedNameOptions, setFeedNameOptions] = useState([]);
  const [feedNameMap, setFeedNameMap] = useState({});
  const [feedTypeOptions, setFeedTypeOptions] = useState([]);
  const [feedTypeMap, setFeedTypeMap] = useState({});
  const [offlineSyncTick, setOfflineSyncTick] = useState(0);

  const [feedDialogOpen, setFeedDialogOpen] = useState(false);
  const [feedDialogInitial, setFeedDialogInitial] = useState(null);
  const [feedDialogSaving, setFeedDialogSaving] = useState(false);
  const [feedDialogError, setFeedDialogError] = useState(null);
  const feedTableRef = useRef(null);
  const feedFloatingScrollbarRef = useRef(null);
  const [feedFloatingScrollbar, setFeedFloatingScrollbar] = useState({
    visible: false,
    left: 0,
    width: 0,
    contentWidth: 0,
  });

  const handleAddFeedRow = () => {
    setFeedDialogInitial(null);
    setFeedDialogError(null);
    setFeedDialogOpen(true);
  };

  const handleEditFeedRow = (row) => {
    if (!row) return;
    setFeedDialogInitial({
      id: row.id,
      stage: row.stage ?? "",
      supplier: row.supplier ?? "",
      feedName: row.feedName ?? "",
      feedType: row.feedType ?? "",
      weightKg: row.weightKg ?? "",
      bin1: row.bin1 ?? "",
      bin2: row.bin2 ?? "",
      feedPaper: row.feedPaper ?? "",
      bin1Reading: row.bin1Reading ?? "",
      bin2Reading: row.bin2Reading ?? "",
      feedSpendId: row.feedSpendId ?? null,
      startDate: row.startDateInput ?? "",
      endDate: row.endDateInput ?? "",
    });
    setFeedDialogError(null);
    setFeedDialogOpen(true);
  };

  const closeFeedDialog = () => {
    setFeedDialogOpen(false);
    setFeedDialogSaving(false);
    setFeedDialogError(null);
    setFeedDialogInitial(null);
  };

  const handleFeedStartSort = () => {
    setFeedStartOrder((order) => (order === "asc" ? "desc" : "asc"));
  };

  // Helpers
  const toInputDate = (val) => {
    if (!val) return "";
    const d = new Date(val);
    if (isNaN(d.getTime())) return "";
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const pick = (...vals) => vals.find((v) => v !== undefined && v !== null && `${v}`.length > 0);
  const toIso = (val) => {
    if (!val) return null;
    const d = new Date(val);
    if (isNaN(d.getTime())) return null;
    return d.toISOString();
  };

  const parseBooleanBit = (value) => {
    if (value === undefined || value === null || value === "") return undefined;
    if (typeof value === "boolean") return value;
    if (typeof value === "number") {
      if (value === 1) return true;
      if (value === 0) return false;
    }
    const normalized = String(value).trim().toLowerCase();
    if (["true", "1", "yes", "y"].includes(normalized)) return true;
    if (["false", "0", "no", "n"].includes(normalized)) return false;
    return undefined;
  };

  const getLocalInsightsBit = (data) => {
    const source = Array.isArray(data)
      ? data[0]
      : Array.isArray(data?.data)
      ? data.data[0]
      : Array.isArray(data?.value)
      ? data.value[0]
      : data;
    return parseBooleanBit(
      pick(
        source?.localInsights,
        source?.LocalInsights,
        source?.localinsights,
        source?.localInsight,
        source?.LocalInsight
      )
    );
  };

  const toNumberOrNull = (value) => {
    if (value === undefined || value === null || value === "") return null;
    const num = Number(value);
    return Number.isNaN(num) ? null : num;
  };

  const toNumberIfNumeric = (value) => {
    if (value === undefined || value === null || value === "") return value;
    const num = Number(value);
    return Number.isNaN(num) ? value : num;
  };

  const toApiDateTime = (value) => {
    if (!value) return null;
    const str = String(value).trim();
    if (!str) return null;
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(str)) return str;
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(str)) return `${str}:00`;
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return `${str}T00:00:00`;
    const d = new Date(str);
    if (Number.isNaN(d.getTime())) return str;
    return d.toISOString();
  };

  const toDateInputValue = (value) => {
    if (!value) return "";
    const dateValue = new Date(value);
    if (Number.isNaN(dateValue.getTime())) {
      const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
      return match ? match[1] : "";
    }
    const yyyy = dateValue.getFullYear();
    const mm = String(dateValue.getMonth() + 1).padStart(2, "0");
    const dd = String(dateValue.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const formatDisplayDate = (value) => {
    if (!value) return "";
    const dateValue = new Date(value);
    if (Number.isNaN(dateValue.getTime())) return String(value);
    const yyyy = dateValue.getFullYear();
    const mm = String(dateValue.getMonth() + 1).padStart(2, "0");
    const dd = String(dateValue.getDate()).padStart(2, "0");
    return `${dd}-${mm}-${yyyy}`;
  };

  const buildOptions = (items, { valueKey = "id", labelKey = "description" } = {}) => {
    const list = [];
    const map = {};
    items.forEach((item) => {
      const rawValue = valueKey === "feedname" ? item.feedname ?? item.id : item.id;
      if (rawValue == null) return;
      const value = String(rawValue);
      if (map[value]) return;
      const id = item.id != null ? String(item.id) : value;
      let rawLabel;
      switch (labelKey) {
        case "value":
          rawLabel = item.value ?? item.description ?? item.feedname ?? item.id;
          break;
        case "feedname":
          rawLabel = item.feedname ?? item.value ?? item.description ?? item.id;
          break;
        default:
          rawLabel = item.description ?? item.value ?? item.feedname ?? item.id;
          break;
      }
      if (rawLabel == null || rawLabel === "") return;
      const label = String(rawLabel);
      list.push({ value, label });
      map[value] = label;
    });
    list.sort((a, b) => a.label.localeCompare(b.label));
    return { list, map };
  };

  const OFFLINE_QUEUE_STORAGE_KEY = "OmnioOfflineQueue";
  const OFFLINE_NS_FEED = "SetupCycleFeed";
  const OFFLINE_NS_SETTINGS = "SetupCycleSettings";

  const readOfflineQueue = () => {
    if (typeof window === "undefined") return [];
    try {
      const raw = window.localStorage.getItem(OFFLINE_QUEUE_STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.warn("Unable to read offline queue", error);
      return [];
    }
  };

  const writeOfflineQueue = (queue) => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(OFFLINE_QUEUE_STORAGE_KEY, JSON.stringify(queue));
    } catch (error) {
      console.warn("Unable to update offline queue", error);
    }
  };

  const removeQueueEntry = (queueId) => {
    if (!queueId) return;
    const queue = readOfflineQueue();
    if (!queue.length) return;
    const filtered = queue.filter((entry) => entry && `${entry.id}` !== `${queueId}`);
    if (filtered.length !== queue.length) {
      writeOfflineQueue(filtered);
    }
  };

  const parseQueuePayload = (entry) => {
    if (!entry?.data) return null;
    if (entry.data.type === "json") {
      try {
        return entry.data.body ? JSON.parse(entry.data.body) : null;
      } catch (error) {
        console.warn("Unable to parse queued payload", error);
        return null;
      }
    }
    if (entry.data.type === "string") {
      const raw = entry.data.body;
      if (raw == null) return null;
      try {
        return JSON.parse(raw);
      } catch (error) {
        return { __raw: raw };
      }
    }
    return null;
  };

  const readCachedCollection = (url) => {
    if (typeof window === "undefined") return null;
    try {
      const key = `OmnioCache:get:${url}?`;
      const raw = window.localStorage.getItem(key);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      const data = parsed?.data;
      if (Array.isArray(data)) return data;
      if (Array.isArray(parsed)) return parsed;
      return null;
    } catch (error) {
      console.warn("Unable to read cached collection for", url, error);
      return null;
    }
  };

  const writeCachedCollection = (url, data) => {
    if (typeof window === "undefined") return;
    try {
      const key = `OmnioCache:get:${url}?`;
      window.localStorage.setItem(key, JSON.stringify({ data }));
    } catch (error) {
      console.warn("Unable to write cached collection for", url, error);
    }
  };

  const mergeOfflineRecords = (cachedRows, offlineRecords) => {
    const offlineArray = Array.isArray(offlineRecords) ? offlineRecords : [];
    const offlineRows = offlineArray
      .filter((entry) => !entry?.__deleted)
      .map((entry) => ({ ...entry, __isOffline: true }));
    const deletedIds = new Set(
      offlineArray.filter((entry) => entry?.__deleted).map((entry) => `${entry.id}`)
    );
    const offlineIds = new Set(offlineRows.map((entry) => `${entry.id}`));
    const filteredCached = (Array.isArray(cachedRows) ? cachedRows : []).filter(
      (entry) => !offlineIds.has(`${entry.id}`) && !deletedIds.has(`${entry.id}`)
    );
    return [...offlineRows, ...filteredCached];
  };

  const normalizeForComparison = (value) => {
    if (value === undefined || value === null || value === "") return null;
    if (typeof value === "number") {
      if (!Number.isFinite(value)) return null;
      return Number(value.toFixed(6));
    }
    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value.toISOString();
    }
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed) return null;
      if (/^[+-]?\d+(\.\d+)?$/.test(trimmed)) {
        const num = Number(trimmed);
        if (!Number.isNaN(num)) return Number(num.toFixed(6));
      }
      if (/^\d{4}-\d{2}-\d{2}/.test(trimmed) || trimmed.includes("T")) {
        const parsed = Date.parse(trimmed);
        if (!Number.isNaN(parsed)) return new Date(parsed).toISOString();
      }
      return trimmed;
    }
    if (typeof value === "boolean") return value;
    return value;
  };

  const valuesEqual = (a, b) => {
    const na = normalizeForComparison(a);
    const nb = normalizeForComparison(b);
    if (na === nb) return true;
    if (na == null && nb == null) return true;
    return false;
  };

  const createOfflineMatcher = (fields) => (entry, row) => {
    if (!entry || !row) return false;
    const idMatches =
      entry.id != null &&
      !isTempIdentifier(entry.id) &&
      row.id != null &&
      `${entry.id}` === `${row.id}`;
    const fieldsMatch = fields.every((field) => valuesEqual(entry[field], row[field]));
    if (idMatches) return fieldsMatch;
    return fieldsMatch;
  };

  const matchFeedRow = createOfflineMatcher([
    "stage",
    "supplier",
    "feedName",
    "feedType",
    "weightKg",
    "bin1",
    "bin2",
    "feedPaper",
    "startDateIso",
    "endDateIso",
  ]);

  const matchProductionRow = createOfflineMatcher([
    "cycleId",
    "placementDateIso",
    "catchDateIso",
    "strain",
  ]);

  const makeTempId = () => `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

  const isTempIdentifier = (value) => {
    if (value === undefined || value === null) return false;
    const str = String(value);
    return str.startsWith("tmp-") || str.startsWith("temp-");
  };

  const normalizePersistedId = (value) => {
    if (value === undefined || value === null) return null;
    const numeric = Number(value);
    return Number.isNaN(numeric) ? value : numeric;
  };

  const getOptimisticId = (rawId) => {
    if (rawId === undefined || rawId === null) return null;
    return isTempIdentifier(rawId) ? rawId : normalizePersistedId(rawId);
  };

  const wasQueuedOffline = (response, error) => {
    if (navigator?.onLine === false) return true;
    if (response?.status === 202) return true;
    if (response?.data?.offline || response?.data?.queued) return true;
    if (error) {
      const status = error?.response?.status;
      if (!status && (error?.code === "ERR_NETWORK" || error?.message?.includes("Network"))) {
        return true;
      }
    }
    return false;
  };

  const normalizeFeedDictionary = (dictData) => {
    const dict = Array.isArray(dictData) ? dictData : [];
    const normalizedDict = dict
      .map((item) => {
        const id = pick(item?.id, item?.ID);
        const type = pick(item?.type, item?.Type);
        if (id == null || type == null) return null;
        const value = pick(item?.value, item?.Value);
        const description = pick(item?.description, item?.Description, item?.name, item?.Name);
        const feedname = pick(item?.feedname, item?.Feedname, item?.feedName, item?.FeedName);
        return {
          id: String(id),
          type: String(type),
          value: value != null ? String(value) : null,
          description: description != null ? String(description) : null,
          feedname: feedname != null ? String(feedname) : null,
        };
      })
      .filter(Boolean);

    const stageItems = normalizedDict.filter((item) => item.type === "10");
    const supplierItems = normalizedDict.filter((item) => item.type === "11");
    const feedItems = normalizedDict.filter((item) => item.type === "5");

    return {
      stage: buildOptions(stageItems, { labelKey: "value" }),
      supplier: buildOptions(supplierItems, { labelKey: "value" }),
      feedName: buildOptions(feedItems, { valueKey: "id", labelKey: "value" }),
      feedType: buildOptions(feedItems, { valueKey: "feedname", labelKey: "description" }),
    };
  };

  const mapFeedApiRows = (feedData) => {
    const source = Array.isArray(feedData) ? feedData : [];
    return source.map((row, index) => {
      const id = pick(row?.id, row?.ID, row?.feedId, row?.FeedId, row?.feed_id) ?? `${index}`;
      const stageVal = pick(row?.stageId, row?.stageID, row?.stageid, row?.StageId, row?.StageID);
      const supplierVal = pick(
        row?.supplierId,
        row?.supplierID,
        row?.supplierid,
        row?.SupplierId,
        row?.SupplierID
      );
      const startVal = pick(row?.startDate, row?.startdate, row?.StartDate);
      const endVal = pick(row?.endDate, row?.enddate, row?.EndDate);
      const feedTypeVal = pick(row?.type, row?.Type, row?.feedTypeId, row?.feedTypeID);
      const feedNameIdVal = pick(row?.feedId, row?.FeedId, row?.feed_id);
      const feedNameVal = pick(row?.feedname, row?.Feedname, row?.feedName, row?.FeedName);
      const bin1Val = pick(row?.bin_1, row?.bin1, row?.Bin_1, row?.Bin1);
      const bin2Val = pick(row?.bin_2, row?.bin2, row?.Bin_2, row?.Bin2);
      const bin1ReadingVal = pick(
        row?.bin1_reading,
        row?.bin1Reading,
        row?.Bin1Reading,
        row?.Bin1_Reading
      );
      const bin2ReadingVal = pick(
        row?.bin2_reading,
        row?.bin2Reading,
        row?.Bin2Reading,
        row?.Bin2_Reading
      );
      const feedPaperVal = pick(
        row?.feedpaper,
        row?.feedPaper,
        row?.Feedpaper,
        row?.FeedPaper,
        row?.feed_paper,
        row?.Feed_Paper
      );
      const weightVal = pick(
        row?.weight,
        row?.Weight,
        row?.feedWeight,
        row?.FeedWeight,
        row?.feed_kg
      );
      const feedSpendId = pick(
        row?.feedSpendId,
        row?.FeedSpendId,
        row?.feed_spend_id,
        row?.FeedSpendID
      );
      const binTotal = sumWeightParts(bin1Val, bin2Val, feedPaperVal);

      const feedNameString =
        feedNameIdVal != null
          ? String(feedNameIdVal)
          : feedNameVal != null
          ? String(feedNameVal)
          : "";
      const feedTypeString = feedTypeVal != null ? String(feedTypeVal) : "";
      const startIso = toApiDateTime(startVal);
      const endIso = toApiDateTime(endVal);

      return {
        id: String(id),
        stage: stageVal != null ? String(stageVal) : "",
        supplier: supplierVal != null ? String(supplierVal) : "",
        feedName: feedNameString,
        feedType: feedTypeString,
        weightKg: toWeightDisplay(weightVal),
        totalWeightKg: toWeightDisplay(binTotal ?? weightVal),
        bin1: toIntDisplay(bin1Val),
        bin2: toIntDisplay(bin2Val),
        bin1Reading: toIntDisplay(bin1ReadingVal),
        bin2Reading: toIntDisplay(bin2ReadingVal),
        feedSpendId,
        feedPaper: toIntDisplay(feedPaperVal),
        startDateDisplay: formatDisplayDate(startIso),
        endDateDisplay: formatDisplayDate(endIso),
        startDateInput: toDateInputValue(startIso),
        endDateInput: toDateInputValue(endIso),
        startDateIso: startIso,
        endDateIso: endIso,
      };
    });
  };

  const buildFeedRowFromPayload = (payload, targetId) => {
    const proto = {
      id: targetId,
      stageId: payload.stageId ?? payload.stageid,
      supplierId: payload.supplierId ?? payload.supplierid,
      startDate: payload.startdate ?? payload.startDate,
      endDate: payload.enddate ?? payload.endDate,
      type: payload.type ?? payload.feedType,
      feedId: payload.feedname ?? payload.feedName,
      feedname: payload.feedname ?? payload.feedName,
      weight: payload.weight ?? payload.weightKg,
      bin_1: payload.bin_1 ?? payload.bin1 ?? payload.binOne,
      bin_2: payload.bin_2 ?? payload.bin2 ?? payload.binTwo,
      bin1_reading: payload.bin1_reading ?? payload.bin1Reading,
      bin2_reading: payload.bin2_reading ?? payload.bin2Reading,
      feedSpendId: payload.feedSpendId ?? payload.feed_spend_id,
      feedpaper: payload.feedpaper ?? payload.feedPaper ?? payload.feed_paper,
    };
    const mapped = mapFeedApiRows([proto]);
    return (
      mapped[0] ?? {
        id: targetId,
        stage: "",
        supplier: "",
        feedName: "",
        feedType: "",
        weightKg: "",
        totalWeightKg: "",
        bin1: "",
        bin2: "",
        bin1Reading: "",
        bin2Reading: "",
        feedSpendId: null,
        feedPaper: "",
        startDateDisplay: "",
        endDateDisplay: "",
        startDateInput: "",
        endDateInput: "",
        startDateIso: null,
        endDateIso: null,
      }
    );
  };

  const mapProductionSettingsRows = (data) => {
    const list = Array.isArray(data)
      ? data
      : Array.isArray(data?.data)
      ? data.data
      : Array.isArray(data?.value)
      ? data.value
      : [];
    return list.map((row, index) => {
      const id = pick(
        row?.id,
        row?.ID,
        row?.broilerSettingId,
        row?.BroilerSettingId,
        row?.broilerSettingsId,
        row?.BroilerSettingsId
      );
      const cycleVal = pick(row?.cycleId, row?.CycleID, row?.cycleID, row?.cycleIdRef);
      const placementRaw = pick(row?.placementDate, row?.PlacementDate, row?.placement_date);
      const catchRaw = pick(
        row?.dateFinalCatch,
        row?.finalCatchDate,
        row?.FinalCatchDate,
        row?.final_catch_date
      );
      const strainVal = pick(
        row?.strain,
        row?.Strain,
        row?.managementPlan,
        row?.ManagementPlan,
        row?.breed,
        row?.Breed
      );
      const placementIso = toApiDateTime(placementRaw);
      const catchIso = toApiDateTime(catchRaw);
      return {
        id: id != null ? String(id) : `settings-${index}`,
        cycleId: cycleVal != null ? String(cycleVal) : null,
        placementDateIso: placementIso,
        catchDateIso: catchIso,
        placementDateInput: toDateInputValue(placementIso),
        catchDateInput: toDateInputValue(catchIso),
        strain: strainVal != null ? String(strainVal) : "",
      };
    });
  };

  const buildProductionSettingsRowFromPayload = (payload, targetId) => {
    const mapped = mapProductionSettingsRows([
      {
        id: targetId,
        cycleId: cycleId,
        placementDate: payload.placementDate ?? payload.PlacementDate,
        finalCatchDate: payload.dateFinalCatch ?? payload.finalCatchDate,
        managementPlan: payload.strain ?? payload.ManagementPlan ?? payload.managementPlan,
        strain: payload.strain ?? payload.ManagementPlan ?? payload.managementPlan,
      },
    ]);
    const row = mapped[0] ?? {
      id: targetId,
      cycleId: cycleId != null ? String(cycleId) : null,
      placementDateIso: null,
      catchDateIso: null,
      placementDateInput: "",
      catchDateInput: "",
      strain: "",
    };
    return row;
  };

  const queuePayloadMatchesRecord = (namespace, payload, record) => {
    if (!payload || typeof payload !== "object" || !record) return false;
    if (record?.__deleted) {
      return valuesEqual(payload?.id ?? payload?.ID ?? payload?.Id, record.id);
    }

    switch (namespace) {
      case OFFLINE_NS_FEED: {
        return (
          valuesEqual(payload?.stageId ?? payload?.stageid, record.stage) &&
          valuesEqual(payload?.supplierId ?? payload?.supplierid, record.supplier) &&
          valuesEqual(payload?.feedname ?? payload?.feedName, record.feedName) &&
          valuesEqual(payload?.type ?? payload?.feedType, record.feedType) &&
          valuesEqual(payload?.weight ?? payload?.weightKg, record.weightKg) &&
          valuesEqual(
            payload?.feedpaper ?? payload?.feedPaper ?? payload?.feed_paper,
            record.feedPaper
          ) &&
          valuesEqual(payload?.startdate ?? payload?.startDate, record.startDateIso) &&
          valuesEqual(payload?.enddate ?? payload?.endDate, record.endDateIso)
        );
      }
      case OFFLINE_NS_SETTINGS: {
        return (
          valuesEqual(payload?.cycleId, record.cycleId) &&
          valuesEqual(payload?.placementDate ?? payload?.PlacementDate, record.placementDateIso) &&
          valuesEqual(payload?.dateFinalCatch ?? payload?.finalCatchDate, record.catchDateIso) &&
          valuesEqual(
            payload?.strain ?? payload?.managementPlan ?? payload?.ManagementPlan,
            record.strain
          )
        );
      }
      default:
        return false;
    }
  };

  const updateQueuePayloadForRecord = (namespace, targetId, payload) => {
    if (targetId == null) return;
    const queue = readOfflineQueue();
    if (!queue.length) return;
    const records = readOfflineRecords(namespace, facilityId, cycleId);
    if (!Array.isArray(records) || records.length === 0) return;
    const record = records.find((entry) => entry && `${entry.id}` === `${targetId}`);
    if (!record?.__queueId) return;
    const queueIndex = queue.findIndex((entry) => entry && `${entry.id}` === `${record.__queueId}`);
    if (queueIndex < 0) return;

    const entry = queue[queueIndex];
    if (!entry) return;
    const updatedEntry = {
      ...entry,
      timestamp: Date.now(),
      data: {
        type: "json",
        body: JSON.stringify(payload ?? null),
      },
    };
    const updatedQueue = [...queue];
    updatedQueue[queueIndex] = updatedEntry;
    writeOfflineQueue(updatedQueue);
  };

  const handleQueuedOfflineRequest = (namespace, targetId) => {
    if (targetId == null) return;
    const queue = readOfflineQueue();
    if (!queue.length) return;

    const records = readOfflineRecords(namespace, facilityId, cycleId);
    if (!Array.isArray(records) || records.length === 0) return;
    const recordIndex = records.findIndex((entry) => entry && `${entry.id}` === `${targetId}`);
    if (recordIndex < 0) return;
    const record = records[recordIndex] || {};
    const existingQueueId = record.__queueId ?? null;

    const existingQueueIndex =
      existingQueueId != null
        ? queue.findIndex((entry) => entry && `${entry.id}` === `${existingQueueId}`)
        : -1;

    let matchedIndex = -1;
    for (let idx = queue.length - 1; idx >= 0; idx -= 1) {
      const payload = parseQueuePayload(queue[idx]);
      if (record?.__deleted) {
        if (valuesEqual(payload?.id ?? payload?.ID ?? payload?.Id, record.id)) {
          matchedIndex = idx;
          break;
        }
      } else if (queuePayloadMatchesRecord(namespace, payload, record)) {
        matchedIndex = idx;
        break;
      }
    }

    if (matchedIndex < 0) {
      matchedIndex = existingQueueIndex;
    }
    if (matchedIndex < 0 && queue.length) {
      matchedIndex = queue.length - 1;
    }

    if (matchedIndex < 0) {
      if (existingQueueId != null) {
        const nextRecords = [...records];
        nextRecords[recordIndex] = { ...record, __queueId: null };
        replaceOfflineRecords(namespace, facilityId, cycleId, nextRecords);
      }
      return;
    }

    const matchedEntry = queue[matchedIndex];
    if (!matchedEntry || matchedEntry.id == null) return;

    let updatedQueue = [...queue];
    let nextQueueId = existingQueueId;

    if (existingQueueIndex >= 0) {
      const existingEntry = updatedQueue[existingQueueIndex];
      if (!existingEntry) return;
      const mergedEntry = {
        ...existingEntry,
        url: matchedEntry.url ?? existingEntry.url,
        baseURL: matchedEntry.baseURL ?? existingEntry.baseURL,
        method: matchedEntry.method ?? existingEntry.method,
        headers: matchedEntry.headers ?? existingEntry.headers,
        params: matchedEntry.params ?? existingEntry.params,
        data: matchedEntry.data ?? existingEntry.data,
        timestamp: matchedEntry.timestamp ?? existingEntry.timestamp,
      };
      updatedQueue[existingQueueIndex] = mergedEntry;
      if (matchedIndex !== existingQueueIndex) {
        updatedQueue.splice(matchedIndex, 1);
        if (matchedIndex < existingQueueIndex) {
          // existing entry shifts left by one after removal
        }
      }
    } else {
      nextQueueId = matchedEntry.id;
      if (nextQueueId == null) return;
    }

    writeOfflineQueue(updatedQueue);

    const nextRecords = [...records];
    nextRecords[recordIndex] = { ...record, __queueId: nextQueueId };
    replaceOfflineRecords(namespace, facilityId, cycleId, nextRecords);
  };

  const combineServerRowsWithOffline = (namespace, serverRows, matcher) => {
    const baseRows = Array.isArray(serverRows) ? serverRows : [];
    const storedOffline = readOfflineRecords(namespace, facilityId, cycleId);
    let offlineList = Array.isArray(storedOffline) ? storedOffline.filter(Boolean) : [];
    if (!offlineList.length) return baseRows;

    const queueEntries = readOfflineQueue();
    const queueIds = new Set(
      Array.isArray(queueEntries) ? queueEntries.filter(Boolean).map((entry) => `${entry.id}`) : []
    );

    const filteredOffline = offlineList.filter((entry) => {
      if (!entry?.__queueId) return true;
      return queueIds.has(`${entry.__queueId}`);
    });

    if (filteredOffline.length !== offlineList.length) {
      offlineList = filteredOffline;
      if (offlineList.length) {
        replaceOfflineRecords(namespace, facilityId, cycleId, offlineList);
      } else {
        clearOfflineRecords(namespace, facilityId, cycleId);
        return baseRows;
      }
    }

    const pending = offlineList.filter((entry) => {
      if (!entry) return false;
      if (entry.__deleted) {
        return baseRows.some((row) => row && `${row.id}` === `${entry.id}`);
      }
      const hasPersistedId = entry.id != null && !isTempIdentifier(entry.id);
      if (hasPersistedId) {
        const matchedById = baseRows.some((row) => row && `${row.id}` === `${entry.id}`);
        if (matchedById) {
          return false;
        }
      }
      return !baseRows.some((row) => matcher(entry, row));
    });

    if (pending.length === 0) {
      clearOfflineRecords(namespace, facilityId, cycleId);
      return baseRows;
    }

    if (pending.length !== offlineList.length) {
      replaceOfflineRecords(namespace, facilityId, cycleId, pending);
    }

    return mergeOfflineRecords(baseRows, pending);
  };

  const applyOptimisticFeedUpsert = (payload, existingId) => {
    const targetId = existingId != null ? existingId : makeTempId();
    let persistedRowData = null;
    setFeedRows((prev) => {
      const next = Array.isArray(prev) ? [...prev] : [];
      const index = next.findIndex((row) => `${row.id}` === `${targetId}`);
      const existingQueueId = index >= 0 ? next[index]?.__queueId : undefined;
      const rowData = {
        ...buildFeedRowFromPayload(payload, targetId),
        __isOffline: true,
        __pendingCreate: existingId == null || isTempIdentifier(existingId),
        __pendingUpdate: existingId != null && !isTempIdentifier(existingId),
        __queueId: existingQueueId,
      };
      persistedRowData = rowData;
      if (index >= 0) {
        next[index] = { ...next[index], ...rowData };
      } else {
        next.unshift(rowData);
      }
      return next;
    });
    if (persistedRowData) {
      upsertOfflineRecord(OFFLINE_NS_FEED, facilityId, cycleId, persistedRowData);
    }
    return targetId;
  };

  const applyOptimisticFeedDelete = (row) => {
    if (!row) return;
    const idString = `${row.id}`;
    setFeedRows((prev) =>
      Array.isArray(prev) ? prev.filter((entry) => `${entry.id}` !== idString) : []
    );
    if (isTempIdentifier(idString) || row?.__pendingCreate) {
      if (row?.__queueId) {
        removeQueueEntry(row.__queueId);
      }
      removeOfflineRecord(OFFLINE_NS_FEED, facilityId, cycleId, row.id);
    } else {
      upsertOfflineRecord(OFFLINE_NS_FEED, facilityId, cycleId, {
        id: row.id,
        __deleted: true,
        __isOffline: true,
        __queueId: row?.__queueId ?? null,
      });
    }
  };

  const applyOptimisticSettingsUpsert = (payload, existingId) => {
    const targetId = existingId != null ? existingId : makeTempId();
    const existingRecords = readOfflineRecords(OFFLINE_NS_SETTINGS, facilityId, cycleId);
    const existingEntry =
      Array.isArray(existingRecords) &&
      existingRecords.find((entry) => entry && `${entry.id}` === `${targetId}`);
    const rowData = {
      ...buildProductionSettingsRowFromPayload(payload, targetId),
      __isOffline: true,
      __pendingCreate: existingId == null || isTempIdentifier(existingId),
      __pendingUpdate: existingId != null && !isTempIdentifier(existingId),
      __queueId: existingEntry?.__queueId ?? null,
    };
    upsertOfflineRecord(OFFLINE_NS_SETTINGS, facilityId, cycleId, rowData);
    return targetId;
  };

  const handleRemoveFeedRow = async (rowId) => {
    const row = feedRows.find((r) => `${r.id}` === `${rowId}`);
    if (!row) return;
    const idString = `${row.id}`;
    if (isTempIdentifier(idString) || row?.__pendingCreate) {
      applyOptimisticFeedDelete(row);
      setSnackMessage("Feed record removed locally.");
      setSnackOpen(true);
      return;
    }

    const requestPayload = { id: toNumberOrNull(row.id) ?? row.id };

    let response;
    try {
      response = await axios.post(removeFeedUrl(), requestPayload, {
        headers: { "Content-Type": "application/json" },
      });
    } catch (error) {
      if (wasQueuedOffline(null, error)) {
        applyOptimisticFeedDelete(row);
        handleQueuedOfflineRequest(OFFLINE_NS_FEED, row.id);
        setSnackMessage("Feed removal queued. It will sync when you're online.");
        setSnackOpen(true);
      } else {
        console.error("Failed to remove feed row", error);
        setSnackMessage("Failed to remove feed record");
        setSnackOpen(true);
      }
      return;
    }

    if (wasQueuedOffline(response)) {
      applyOptimisticFeedDelete(row);
      handleQueuedOfflineRequest(OFFLINE_NS_FEED, row.id);
      setSnackMessage("Feed removal queued. It will sync when you're online.");
      setSnackOpen(true);
      return;
    }

    setFeedRows((rows) => rows.filter((r) => `${r.id}` !== idString));
    removeOfflineRecord(OFFLINE_NS_FEED, facilityId, cycleId, row.id);
    setSnackMessage("Feed record removed successfully");
    setSnackOpen(true);
    setReloadKey((key) => key + 1);
  };

  const handleFeedDialogSave = async (values) => {
    const existingIdValue = values?.id ?? null;
    const optimisticId = getOptimisticId(existingIdValue);
    const resolvedExistingId =
      existingIdValue != null && !isTempIdentifier(existingIdValue)
        ? normalizePersistedId(existingIdValue)
        : null;

    const currentDateValue = toApiDateTime(values.startDate);
    const currentDateTime = currentDateValue ? new Date(currentDateValue).getTime() : Number.NaN;
    const previousReadingRow = [...feedRows]
      .filter((row) => {
        if (`${row.id}` === `${existingIdValue}`) return false;
        const rowDateTime = row.startDateIso ? new Date(row.startDateIso).getTime() : Number.NaN;
        return (
          Number.isFinite(rowDateTime) &&
          (!Number.isFinite(currentDateTime) || rowDateTime <= currentDateTime)
        );
      })
      .sort((a, b) => new Date(b.startDateIso).getTime() - new Date(a.startDateIso).getTime())[0];
    const existingRow = feedRows.find((row) => `${row.id}` === `${existingIdValue}`);
    const calculateBinDifference = (readingKey, binKey) => {
      const currentReading = toNumberOrNull(values[readingKey]);
      const previousReading = toNumberOrNull(previousReadingRow?.[readingKey]);
      if (currentReading != null && previousReading != null) {
        const difference = currentReading - previousReading;
        return {
          consumption: Math.max(0, -difference),
          increase: Math.max(0, difference),
        };
      }
      return {
        consumption: 0,
        increase: toNumberOrNull(existingRow?.[binKey]) ?? 0,
      };
    };
    const bin1Difference = calculateBinDifference("bin1Reading", "bin1");
    const bin2Difference = calculateBinDifference("bin2Reading", "bin2");
    const bin1Consumption = bin1Difference.consumption;
    const bin2Consumption = bin2Difference.consumption;
    const bin1Increase = bin1Difference.increase;
    const bin2Increase = bin2Difference.increase;
    const totalConsumption = bin1Consumption + bin2Consumption;
    const totalIncrease = bin1Increase + bin2Increase;

    const sharedPayload = {
      broilerId: toNumberOrNull(facilityId) ?? facilityId,
      cycleId: toNumberOrNull(cycleId) ?? cycleId,
      farmId: toNumberOrNull(farmId) ?? farmId,
      stageId: toNumberOrNull(values.stage),
      supplierId: toNumberOrNull(values.supplier),
      startdate: currentDateValue,
      type: toNumberOrNull(values.feedType),
      feedname: toNumberOrNull(values.feedName),
      weight: totalIncrease,
    };
    const optimisticPayload = {
      ...(resolvedExistingId != null
        ? { id: resolvedExistingId, ID: resolvedExistingId, Id: resolvedExistingId }
        : {}),
      ...sharedPayload,
      enddate: toApiDateTime(values.endDate),
      bin_1: bin1Increase,
      bin_2: bin2Increase,
      feedpaper: toNumberOrNull(values.feedPaper),
      bin1_reading: toNumberOrNull(values.bin1Reading),
      bin2_reading: toNumberOrNull(values.bin2Reading),
    };
    const feedTotalPayload = {
      ...(resolvedExistingId != null ? { id: resolvedExistingId } : {}),
      ...sharedPayload,
      bin1_reading: optimisticPayload.bin1_reading,
      bin2_reading: optimisticPayload.bin2_reading,
      bin_1: optimisticPayload.bin_1,
      bin_2: optimisticPayload.bin_2,
    };

    setFeedDialogSaving(true);
    setFeedDialogError(null);

    let response;
    try {
      response = await axios.post(updateFeedTotalUrl(), feedTotalPayload, {
        headers: { "Content-Type": "application/json" },
      });
    } catch (error) {
      if (wasQueuedOffline(null, error)) {
        const targetId = applyOptimisticFeedUpsert(optimisticPayload, optimisticId ?? null);
        handleQueuedOfflineRequest(OFFLINE_NS_FEED, targetId);
        updateQueuePayloadForRecord(OFFLINE_NS_FEED, targetId, feedTotalPayload);
        await onFeedSaved?.();
        setFeedDialogSaving(false);
        closeFeedDialog();
        setSnackMessage("Feed record saved locally. It will sync when you're online.");
        setSnackOpen(true);
        return;
      }
      console.error("Failed to save feed total", error);
      setFeedDialogSaving(false);
      setFeedDialogError("Failed to save feed record");
      return;
    }

    if (wasQueuedOffline(response)) {
      const targetId = applyOptimisticFeedUpsert(optimisticPayload, optimisticId ?? null);
      handleQueuedOfflineRequest(OFFLINE_NS_FEED, targetId);
      updateQueuePayloadForRecord(OFFLINE_NS_FEED, targetId, feedTotalPayload);
      await onFeedSaved?.();
      setFeedDialogSaving(false);
      closeFeedDialog();
      setSnackMessage("Feed record saved locally. It will sync when you're online.");
      setSnackOpen(true);
      return;
    }

    const consumptionRequests = [
      { amount: bin1Consumption, bin: "bin1" },
      { amount: bin2Consumption, bin: "bin2" },
    ].filter(({ amount }) => amount > 0);
    let consumptionUpdateFailed = false;
    try {
      await Promise.all(
        consumptionRequests.map(({ amount, bin }) =>
          axios.post(
            updateFeedWaterUrl(),
            {
              broilerID: toNumberOrNull(facilityId) ?? facilityId,
              farmId: toNumberOrNull(farmId) ?? farmId,
              cycleId: toNumberOrNull(cycleId) ?? cycleId,
              feed: amount,
              dateTime: sharedPayload.startdate,
              stage: sharedPayload.stageId,
              feedName: sharedPayload.feedname,
              bin_1: bin === "bin1",
              bin_2: bin === "bin2",
            },
            { headers: { "Content-Type": "application/json", Accept: "application/json" } }
          )
        )
      );
    } catch (consumptionError) {
      consumptionUpdateFailed = true;
      console.error("Failed to update feed consumption and spend", consumptionError);
    }

    await onFeedSaved?.();
    setFeedDialogSaving(false);
    closeFeedDialog();
    setSnackMessage(
      consumptionUpdateFailed
        ? "Feed record saved, but consumption and spend could not be updated"
        : existingIdValue && !isTempIdentifier(existingIdValue)
        ? "Feed record updated successfully"
        : "Feed record added successfully"
    );
    setSnackOpen(true);
    setReloadKey((key) => key + 1);
  };

  const selectedKeys = useMemo(
    () => ({ facilityId, cycleId, farmId, reloadKey, offlineSyncTick }),
    [facilityId, cycleId, farmId, reloadKey, offlineSyncTick]
  );

  useEffect(() => {
    resetProductionCycleFields();
  }, [facilityId, cycleId]);

  useEffect(() => {
    if (typeof window === "undefined") return () => {};
    const handleOfflineSync = () => {
      setOfflineSyncTick((tick) => tick + 1);
    };
    window.addEventListener("omnio:offline-sync-complete", handleOfflineSync);
    return () => window.removeEventListener("omnio:offline-sync-complete", handleOfflineSync);
  }, []);

  useEffect(() => {
    const shouldLoad = openDialog === "feed" && facilityId != null && cycleId != null;
    if (!shouldLoad) return;

    let didCancel = false;
    const applyDictionarySets = (dictSets) => {
      if (didCancel || !dictSets) return;
      setFeedStageOptions(dictSets.stage.list);
      setFeedStageMap(dictSets.stage.map);
      setFeedSupplierOptions(dictSets.supplier.list);
      setFeedSupplierMap(dictSets.supplier.map);
      setFeedNameOptions(dictSets.feedName.list);
      setFeedNameMap(dictSets.feedName.map);
      setFeedTypeOptions(dictSets.feedType.list);
      setFeedTypeMap(dictSets.feedType.map);
    };

    const hydrateFromCache = () => {
      const cachedDict = normalizeFeedDictionary(
        readCachedCollection(broilerDataByFarmUrl(farmId))
      );
      const cachedFeed = mapFeedApiRows(readCachedCollection(feedScheduleUrl(facilityId, cycleId)));
      const merged = combineServerRowsWithOffline(OFFLINE_NS_FEED, cachedFeed, matchFeedRow);
      applyDictionarySets(cachedDict);
      if (!didCancel) {
        setFeedError(null);
        setFeedRows(merged);
        setFeedLoading(false);
      }
    };

    const loadFeed = async () => {
      setFeedLoading(true);
      setFeedError(null);

      const offline = typeof navigator !== "undefined" && navigator.onLine === false;
      if (offline) {
        hydrateFromCache();
        return;
      }

      try {
        const [feedRes, dictRes] = await Promise.all([
          axios.get(feedScheduleUrl(facilityId, cycleId)),
          axios.get(broilerDataByFarmUrl(farmId)),
        ]);
        if (didCancel) return;
        writeCachedCollection(feedScheduleUrl(facilityId, cycleId), feedRes.data);
        writeCachedCollection(broilerDataByFarmUrl(farmId), dictRes.data);

        const dictSets = normalizeFeedDictionary(dictRes.data);
        applyDictionarySets(dictSets);

        const feedData = Array.isArray(feedRes.data) ? feedRes.data : [];
        const normalizedRows = mapFeedApiRows(feedData);
        if (!didCancel) {
          const merged = combineServerRowsWithOffline(
            OFFLINE_NS_FEED,
            normalizedRows,
            matchFeedRow
          );
          setFeedRows(merged);
        }
      } catch (err) {
        console.error("Failed to load feed data", err);
        if (!didCancel) {
          setFeedError("Failed to load feed data");
          hydrateFromCache();
        }
      } finally {
        if (!didCancel) {
          setFeedLoading(false);
        }
      }
    };

    loadFeed();
    return () => {
      didCancel = true;
    };
  }, [openDialog, facilityId, cycleId, farmId, reloadKey, offlineSyncTick]);

  // Fetch and hydrate whenever dialog opens or selection changes
  useEffect(() => {
    const shouldLoad = openDialog === "dates" && facilityId != null && cycleId != null;
    if (!shouldLoad) return;
    let didCancel = false;

    const applySettingsRow = (row) => {
      if (!row) {
        resetProductionCycleFields();
        return;
      }
      setPlacementDate(row.placementDateInput ?? "");
      setFinalCatchDate(row.catchDateInput ?? "");
      setSettingId(row.id ?? null);
      setManagementPlan(row.strain ?? "");
    };

    const hydrateSettings = () => {
      const cached = mapProductionSettingsRows(
        readCachedCollection(broilerSettingsUrl(facilityId))
      );
      const merged = combineServerRowsWithOffline(OFFLINE_NS_SETTINGS, cached, matchProductionRow);
      const match = merged.find((r) => `${r.cycleId}` === `${cycleId}`);
      setError(null);
      applySettingsRow(match);
      setLoading(false);
    };

    const load = async () => {
      setLoading(true);
      setError(null);

      const offline = typeof navigator !== "undefined" && navigator.onLine === false;
      if (offline) {
        hydrateSettings();
        return;
      }

      try {
        const res = await axios.get(broilerSettingsUrl(facilityId));
        if (didCancel) return;
        writeCachedCollection(broilerSettingsUrl(facilityId), res.data);
        const normalizedRows = mapProductionSettingsRows(res.data);
        const merged = combineServerRowsWithOffline(
          OFFLINE_NS_SETTINGS,
          normalizedRows,
          matchProductionRow
        );
        const match = merged.find((r) => `${r.cycleId}` === `${cycleId}`);
        applySettingsRow(match);

        try {
          const strainRes = await axios.get(farmStrainByFarmUrl(farmId));
          writeCachedCollection(farmStrainByFarmUrl(farmId), strainRes.data);
          const strains = Array.isArray(strainRes.data) ? strainRes.data : [];
          const mpMap = strains.reduce((acc, item) => {
            const strainId = item?.strainId ?? item?.strainID ?? item?.StrainID;
            const name = item?.name ?? item?.Name;
            if (strainId != null && name != null) acc[String(strainId)] = String(name);
            return acc;
          }, {});
          setManagementPlanMap(mpMap);
          setManagementPlanOptions(Object.keys(mpMap));
        } catch (dictError) {
          // Non-critical: ignore
        }
      } catch (e) {
        console.error("Failed to load broiler settings", e);
        if (!didCancel) {
          setError("Failed to load broiler settings");
          hydrateSettings();
        }
      } finally {
        if (!didCancel) setLoading(false);
      }
    };

    load();
    return () => {
      didCancel = true;
    };
  }, [openDialog, selectedKeys]);

  const sortedFeedRows = useMemo(() => {
    const rows = Array.isArray(feedRows) ? [...feedRows] : [];
    const direction = feedStartOrder === "asc" ? 1 : -1;
    const chronologicalRows = rows.sort((a, b) => {
      const aTime = a?.startDateIso ? new Date(a.startDateIso).getTime() : null;
      const bTime = b?.startDateIso ? new Date(b.startDateIso).getTime() : null;
      const aHasDate = Number.isFinite(aTime);
      const bHasDate = Number.isFinite(bTime);

      if (!aHasDate && !bHasDate) return 0;
      if (!aHasDate) return 1;
      if (!bHasDate) return -1;
      return aTime - bTime;
    });
    const rowsWithConsumption = chronologicalRows.map((row, index) => {
      const previousRow = index > 0 ? chronologicalRows[index - 1] : null;
      const calculateConsumption = (readingKey) => {
        const reading = toNumberOrNull(row?.[readingKey]);
        const previousReading = toNumberOrNull(previousRow?.[readingKey]);
        return reading != null && previousReading != null
          ? Math.max(0, previousReading - reading)
          : 0;
      };
      const bin1Consumption = calculateConsumption("bin1Reading");
      const bin2Consumption = calculateConsumption("bin2Reading");
      return {
        ...row,
        calculatedBin1Consumption: bin1Consumption,
        calculatedBin2Consumption: bin2Consumption,
        calculatedTotalConsumption: bin1Consumption + bin2Consumption,
      };
    });
    return direction === 1 ? rowsWithConsumption : rowsWithConsumption.reverse();
  }, [feedRows, feedStartOrder]);

  useEffect(() => {
    const tableContainer = feedTableRef.current;
    if (!tableContainer || openDialog !== "feed") return undefined;

    const updateFloatingScrollbar = () => {
      const rect = tableContainer.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const left = Math.max(0, rect.left);
      const width = Math.max(0, Math.min(rect.right, viewportWidth) - left);
      const visible =
        tableContainer.scrollWidth > tableContainer.clientWidth &&
        rect.top < viewportHeight &&
        rect.bottom > viewportHeight;

      setFeedFloatingScrollbar({
        visible,
        left,
        width,
        contentWidth: tableContainer.scrollWidth,
      });

      if (feedFloatingScrollbarRef.current) {
        feedFloatingScrollbarRef.current.scrollLeft = tableContainer.scrollLeft;
      }
    };

    const handleViewportScroll = (event) => {
      if (event.target === tableContainer || event.target === feedFloatingScrollbarRef.current) {
        return;
      }
      updateFloatingScrollbar();
    };

    const resizeObserver = new ResizeObserver(updateFloatingScrollbar);
    resizeObserver.observe(tableContainer);
    window.addEventListener("resize", updateFloatingScrollbar);
    window.addEventListener("scroll", handleViewportScroll, true);
    updateFloatingScrollbar();

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateFloatingScrollbar);
      window.removeEventListener("scroll", handleViewportScroll, true);
    };
  }, [openDialog]);

  const handleFeedTableScroll = (event) => {
    if (feedFloatingScrollbarRef.current) {
      feedFloatingScrollbarRef.current.scrollLeft = event.currentTarget.scrollLeft;
    }
  };

  const handleFeedFloatingScrollbarScroll = (event) => {
    if (feedTableRef.current) {
      feedTableRef.current.scrollLeft = event.currentTarget.scrollLeft;
    }
  };

  useEffect(() => {
    if (openDialog !== "placement-list") return undefined;
    if (!facilityId || !cycleId) {
      setPlacements([]);
      return undefined;
    }
    let active = true;
    const loadPlacements = async () => {
      setPlacementsLoading(true);
      setPlacementsError("");
      try {
        const [placementResponse, dictionaryResult] = await Promise.all([
          axios.get(hatcheryDataUrl(facilityId, cycleId)),
          axios.get(allFarmsUrl()).catch(() => null),
        ]);
        if (!active) return;
        const payload = placementResponse.data;
        const records = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload?.value)
          ? payload.value
          : Array.isArray(payload?.result)
          ? payload.result
          : [];
        const dictionaryPayload = dictionaryResult?.data;
        const dictionary = Array.isArray(dictionaryPayload)
          ? dictionaryPayload
          : dictionaryPayload?.data ?? [];
        const hatcheryNames = new Map(
          dictionary
            .filter(
              (item) =>
                (item?.id != null || item?.ID != null) &&
                String(item?.sectorType ?? item?.SectorType) === "4"
            )
            .map((item) => [String(item?.id ?? item?.ID), item?.name ?? item?.Name])
        );
        setPlacements(
          records
            .filter((record) => {
              const recordFarmId = pick(record?.farmId, record?.FarmID, record?.farmID);
              const recordFacilityId = pick(
                record?.broilerId,
                record?.BroilerID,
                record?.facilityId,
                record?.FacilityID
              );
              const recordCycleId = pick(record?.cycleId, record?.CycleID, record?.cycleID);
              return (
                (recordFarmId == null || String(recordFarmId) === String(farmId)) &&
                (recordFacilityId == null || String(recordFacilityId) === String(facilityId)) &&
                (recordCycleId == null || String(recordCycleId) === String(cycleId))
              );
            })
            .map((record, index) => {
              const hatcheryId = pick(record?.hatcheryName, record?.HatcheryName);
              return {
                id: pick(
                  record?.id,
                  record?.ID,
                  record?.hatcheryId,
                  record?.HatcheryId,
                  record?.hatchery_id,
                  index
                ),
                date: pick(record?.date, record?.Date),
                invoiceNumber: pick(record?.invoiceNumber, record?.InvoiceNumber) ?? "",
                hatchery: hatcheryNames.get(String(hatcheryId)) ?? hatcheryId ?? "",
                noOfBoxes: pick(record?.totalNoOfBoxes, record?.TotalNoOfBoxes) ?? "",
              };
            })
        );
      } catch (error) {
        if (!active) return;
        setPlacements([]);
        setPlacementsError(
          error?.response?.data?.message || error?.message || "Unable to load placements."
        );
      } finally {
        if (active) setPlacementsLoading(false);
      }
    };
    loadPlacements();
    return () => {
      active = false;
    };
  }, [openDialog, facilityId, cycleId, farmId, placementRefreshCount]);

  const FeedContainer = embeddedFeed ? Box : Dialog;

  return (
    <>
      {!embeddedFeed && (
        <>
          <Button
            fullWidth
            variant="outlined"
            sx={{ color: "grey.600" }}
            onClick={() => setOpenDialog("dates")}
            disabled={!facilityId || !cycleId}
          >
            Production Cycle
          </Button>

          <Button
            fullWidth
            variant="outlined"
            sx={{ mt: 2, color: "grey.600" }}
            onClick={() => setOpenDialog("placement-list")}
          >
            Placement
          </Button>

          <Button
            fullWidth
            variant="outlined"
            sx={{ mt: 2, color: "grey.600" }}
            onClick={() => setOpenDialog("activities")}
          >
            Scheduled Activities
          </Button>

          <Button fullWidth variant="outlined" sx={{ mt: 2, color: "grey.600" }} disabled>
            UTILITIES (coming soon)
          </Button>
        </>
      )}

      {/* Dialogs */}
      <Dialog open={openDialog === "dates"} onClose={() => setOpenDialog(null)}>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box component="span" sx={{ fontWeight: 600 }}>
              Production Cycle
            </Box>
            <Button
              variant="contained"
              size="small"
              disabled={(() => {
                const isManagementPlanMissing =
                  (!managementPlan || `${managementPlan}`.trim() === "") &&
                  managementPlanOptions.length > 0;
                return (
                  !facilityId ||
                  !cycleId ||
                  !placementDate ||
                  !finalCatchDate ||
                  isManagementPlanMissing ||
                  saving
                );
              })()}
              onClick={async () => {
                const existingIdValue = settingId;
                const optimisticId = getOptimisticId(existingIdValue);
                const resolvedExistingId =
                  existingIdValue != null && !isTempIdentifier(existingIdValue)
                    ? normalizePersistedId(existingIdValue)
                    : null;
                const selectedStrainId =
                  managementPlanOptions.find(
                    (option) => String(option) === String(managementPlan)
                  ) ?? managementPlan;
                const normalizedStrainId = Number.isNaN(Number(selectedStrainId))
                  ? selectedStrainId
                  : Number(selectedStrainId);
                setSaving(true);
                setError(null);
                const isOffline = typeof navigator !== "undefined" && navigator.onLine === false;

                let localInsights;
                if (!isOffline && farmId != null && facilityId != null) {
                  try {
                    const broilerResponse = await axios.get(
                      broilersByFarmAndFacilityUrl(farmId, facilityId)
                    );
                    localInsights = getLocalInsightsBit(broilerResponse.data);
                  } catch (broilerError) {
                    console.warn("Failed to load broiler localInsights", broilerError);
                  }
                }

                const payload = {
                  ...(resolvedExistingId != null ? { id: resolvedExistingId } : {}),
                  broilerId: toNumberIfNumeric(facilityId),
                  cycleId: toNumberIfNumeric(cycleId),
                  placementDate: toApiDateTime(placementDate),
                  heatingSystemsType: null,
                  ventalationSystem: null,
                  drinkerSystemType: null,
                  lightingProgram: null,
                  litterType: null,
                  healthType: null,
                  sanitationProgram: null,
                  dateFinalCatch: toApiDateTime(finalCatchDate),
                  vaccination: null,
                  strain: managementPlan ? normalizedStrainId : null,
                  localInsights: localInsights ?? false,
                };

                try {
                  const response = await axios.post(updateBroilerSettingsUrl(), payload, {
                    headers: { "Content-Type": "application/json" },
                  });

                  if (isOffline || wasQueuedOffline(response)) {
                    const targetId = applyOptimisticSettingsUpsert(payload, optimisticId ?? null);
                    handleQueuedOfflineRequest(OFFLINE_NS_SETTINGS, targetId);
                    updateQueuePayloadForRecord(OFFLINE_NS_SETTINGS, targetId, payload);
                    setSettingId(targetId);
                    setPlacementDate(toDateInputValue(payload.placementDate));
                    setFinalCatchDate(toDateInputValue(payload.dateFinalCatch));
                    setManagementPlan(
                      payload.strain != null ? String(payload.strain) : managementPlan
                    );
                    setSnackMessage(
                      "Production Cycle saved locally. It will sync when you're online."
                    );
                    setSnackOpen(true);
                    setOpenDialog(null);
                    setSaving(false);
                    return;
                  }

                  clearOfflineRecords(OFFLINE_NS_SETTINGS, facilityId, cycleId);

                  try {
                    const res2 = await axios.get(broilerSettingsUrl(facilityId));
                    const data2 = Array.isArray(res2.data) ? res2.data : [];
                    const normalizedRows = mapProductionSettingsRows(data2);
                    const match2 = normalizedRows.find((r) => `${r.cycleId}` === `${cycleId}`);
                    setPlacementDate(match2?.placementDateInput ?? "");
                    setFinalCatchDate(match2?.catchDateInput ?? "");
                    setSettingId(match2?.id ?? null);
                    setManagementPlan(match2?.strain ?? "");
                  } catch {}

                  setSnackMessage("Production Cycle saved successfully");
                  setSnackOpen(true);
                  setOpenDialog(null);
                } catch (error) {
                  if (isOffline || wasQueuedOffline(null, error)) {
                    const targetId = applyOptimisticSettingsUpsert(payload, optimisticId ?? null);
                    handleQueuedOfflineRequest(OFFLINE_NS_SETTINGS, targetId);
                    updateQueuePayloadForRecord(OFFLINE_NS_SETTINGS, targetId, payload);
                    setSettingId(targetId);
                    setPlacementDate(toDateInputValue(payload.placementDate));
                    setFinalCatchDate(toDateInputValue(payload.dateFinalCatch));
                    setManagementPlan(
                      payload.strain != null ? String(payload.strain) : managementPlan
                    );
                    setSnackMessage(
                      "Production Cycle saved locally. It will sync when you're online."
                    );
                    setSnackOpen(true);
                    setOpenDialog(null);
                  } else {
                    console.error("Failed to save broiler settings", error);
                    setError("Failed to save broiler settings");
                  }
                } finally {
                  setSaving(false);
                }
              }}
              sx={{ color: "#fff" }}
            >
              Save
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <Box p={2} display="flex" justifyContent="center">
              <CircularProgress size={24} />
            </Box>
          ) : error ? (
            <Box p={2}>{error}</Box>
          ) : (
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Placement date</TableCell>
                  <TableCell>
                    <TextField
                      type="date"
                      value={placementDate}
                      onChange={(e) => setPlacementDate(e.target.value)}
                      fullWidth
                      size="small"
                      sx={{
                        height: "1cm",
                        "& .MuiOutlinedInput-root, & .MuiInputBase-root": {
                          height: "1cm !important",
                        },
                        "& input": {
                          height: "1cm !important",
                          padding: "0 14px",
                          fontSize: "1rem",
                        },
                      }}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Catch date</TableCell>
                  <TableCell>
                    <TextField
                      type="date"
                      value={finalCatchDate}
                      onChange={(e) => setFinalCatchDate(e.target.value)}
                      fullWidth
                      size="small"
                      sx={{
                        height: "1cm",
                        "& .MuiOutlinedInput-root, & .MuiInputBase-root": {
                          height: "1cm !important",
                        },
                        "& input": {
                          height: "1cm !important",
                          padding: "0 14px",
                          fontSize: "1rem",
                        },
                      }}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Management plan</TableCell>
                  <TableCell>
                    <TextField
                      select
                      value={managementPlan}
                      onChange={(e) => setManagementPlan(String(e.target.value))}
                      fullWidth
                      size="small"
                      sx={{
                        height: "1cm",
                        "& .MuiOutlinedInput-root, & .MuiInputBase-root": {
                          height: "1cm !important",
                        },
                        "& .MuiSelect-select": {
                          minHeight: "1cm !important",
                          lineHeight: "1cm !important",
                          display: "flex",
                          alignItems: "center",
                          py: 0,
                          fontSize: "1rem",
                        },
                      }}
                      SelectProps={{
                        renderValue: (value) => managementPlanMap[String(value)] || String(value),
                      }}
                    >
                      {managementPlanOptions.length === 0 ? (
                        <MenuItem value="" disabled>
                          No options
                        </MenuItem>
                      ) : (
                        managementPlanOptions.map((opt) => {
                          const label = managementPlanMap[String(opt)] || String(opt);
                          return (
                            <MenuItem key={opt} value={opt}>
                              {label}
                            </MenuItem>
                          );
                        })
                      )}
                    </TextField>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackOpen}
        autoHideDuration={2500}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={
          isSmallScreen
            ? {}
            : {
                left: "50% !important",
                top: "50% !important",
                bottom: "auto !important",
                transform: "translate(-50%, -50%)",
              }
        }
      >
        <Alert onClose={() => setSnackOpen(false)} severity="success" sx={{ width: "100%" }}>
          {snackMessage}
        </Alert>
      </Snackbar>

      <Dialog
        open={openDialog === "placement-list"}
        onClose={() => setOpenDialog(null)}
        fullWidth
        maxWidth="lg"
        PaperProps={{ sx: { minHeight: "70vh" } }}
      >
        <DialogTitle
          sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pr: 6 }}
        >
          Invoices
          <IconButton size="small" aria-label="close" onClick={() => setOpenDialog(null)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{ position: "relative", pb: 2, px: { xs: 1, sm: 2 }, overflowX: "hidden" }}
        >
          {placementsError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {placementsError}
            </Alert>
          )}
          {placementsLoading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <TableContainer
              component={Box}
              sx={{
                boxShadow: "none",
                width: "fit-content",
                maxWidth: "100%",
                mx: "auto",
                overflowX: "auto",
              }}
            >
              <Table
                size="small"
                aria-label="Invoices table"
                sx={{
                  tableLayout: "fixed",
                  width: "19.25cm",
                  minWidth: "19.25cm",
                  borderCollapse: "collapse",
                  "& th, & td": {
                    border: "1px solid",
                    borderColor: "grey.300",
                    fontSize: "0.9rem",
                    lineHeight: 1.2,
                    p: 0.5,
                    textAlign: "center",
                    color: "text.primary",
                  },
                }}
              >
                <colgroup>
                  <col style={{ width: "4.35cm" }} />
                  <col style={{ width: "4.3cm" }} />
                  <col style={{ width: "5.2cm" }} />
                  <col style={{ width: "3.4cm" }} />
                  <col style={{ width: "2cm" }} />
                </colgroup>
                <TableBody>
                  <TableRow sx={{ height: "0.96cm", bgcolor: "grey.100" }}>
                    <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Invoice No.</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Hatchery</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>No. of Boxes</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>
                      <IconButton
                        size="small"
                        aria-label="add"
                        disabled={!facilityId || !cycleId}
                        onClick={() => {
                          setSelectedHatcheryDataId("new");
                          setSelectedPlacementInvoice(null);
                          setOpenDialog("inputs");
                        }}
                        sx={{
                          width: 28,
                          height: 28,
                          bgcolor: "grey.100",
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "grey.800",
                          "&:hover": { bgcolor: "grey.200" },
                        }}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  {placements.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 2, color: "text.secondary" }}>
                        No data
                      </TableCell>
                    </TableRow>
                  ) : (
                    placements.map((placement) => (
                      <TableRow key={placement.id} sx={{ height: "0.64cm" }}>
                        <TableCell>{formatDisplayDate(placement.date)}</TableCell>
                        <TableCell>
                          <Link
                            component="button"
                            type="button"
                            underline="hover"
                            onClick={() => {
                              setSelectedHatcheryDataId(placement.id);
                              setSelectedPlacementInvoice(placement.invoiceNumber);
                              setOpenDialog("inputs");
                            }}
                          >
                            {placement.invoiceNumber || "—"}
                          </Link>
                        </TableCell>
                        <TableCell>{placement.hatchery}</TableCell>
                        <TableCell>{placement.noOfBoxes}</TableCell>
                        <TableCell>
                          <Link
                            component="button"
                            type="button"
                            underline="hover"
                            onClick={() => {
                              setSelectedHatcheryDataId(placement.id);
                              setSelectedPlacementInvoice(placement.invoiceNumber);
                              setOpenDialog("inputs");
                            }}
                          >
                            Details
                          </Link>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={openDialog === "inputs"}
        onClose={() => setOpenDialog("placement-list")}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle sx={{ pb: 1.5 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box component="span" sx={{ fontWeight: 600 }}>
              Placement
            </Box>
            <Button
              variant="contained"
              size="small"
              onClick={async () => {
                const ok = await inputsRef.current?.save?.();
                if (ok) {
                  setPlacementRefreshCount((count) => count + 1);
                  setOpenDialog("placement-list");
                  setSnackMessage("Inputs saved successfully");
                  setSnackOpen(true);
                }
              }}
              disabled={!facilityId || !cycleId}
              sx={{ color: "#fff" }}
            >
              Save
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Inputs
            ref={inputsRef}
            facilityId={facilityId}
            cycleId={cycleId}
            farmId={farmId}
            selectedHatcheryDataId={selectedHatcheryDataId}
            selectedInvoiceNumber={selectedPlacementInvoice}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={openDialog === "activities"}
        onClose={() => setOpenDialog(null)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle sx={{ pb: 1.5, position: "relative" }}>
          <Box component="span" sx={{ fontWeight: 600 }}>
            Scheduled Activities
          </Box>
          <IconButton
            aria-label="close"
            size="small"
            title="Close"
            onClick={() => setOpenDialog(null)}
            sx={{ position: "absolute", top: 8, right: 8 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <ProductionActivities facilityId={facilityId} cycleId={cycleId} farmId={farmId} />
        </DialogContent>
      </Dialog>

      <FeedContainer
        open={embeddedFeed ? undefined : openDialog === "feed"}
        onClose={embeddedFeed ? undefined : () => setOpenDialog(null)}
        fullWidth={embeddedFeed ? undefined : true}
        maxWidth={embeddedFeed ? undefined : "lg"}
      >
        {!embeddedFeed && (
          <DialogTitle sx={{ pb: 1.5, position: "relative" }}>
            <Box component="span" sx={{ fontWeight: 600 }}>
              Feed
            </Box>
            <IconButton
              aria-label="close"
              size="small"
              title="Close"
              onClick={() => setOpenDialog(null)}
              sx={{ position: "absolute", top: 8, right: 8 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
        )}
        <DialogContent>
          {feedLoading && (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={20} />
            </Box>
          )}
          {feedError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {feedError}
            </Alert>
          )}
          <Box
            sx={{
              mt: "0.3cm",
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              overflowX: "hidden",
            }}
          >
            <TableContainer
              ref={feedTableRef}
              component={Box}
              onScroll={handleFeedTableScroll}
              sx={{ boxShadow: "none", mx: "auto", overflowX: "auto" }}
            >
              <Table
                size="small"
                sx={{
                  tableLayout: "fixed",
                  mx: "auto",
                  width: "29.352cm",
                  minWidth: "29.352cm",
                  borderCollapse: "collapse",
                  "& th, & td": {
                    border: "1px solid",
                    borderColor: "grey.300",
                    fontSize: "0.9rem",
                    lineHeight: 1.2,
                    p: 0.75,
                    textAlign: "center",
                  },
                  "& th": { bgcolor: "grey.100", fontWeight: 600 },
                }}
              >
                <colgroup>
                  <col style={{ width: "1.958cm" }} />
                  <col style={{ width: "2.448cm" }} />
                  <col style={{ width: "3.048cm" }} />
                  <col style={{ width: "2.558cm" }} />
                  <col style={{ width: "2.65cm" }} />
                  <col style={{ width: "2.65cm" }} />
                  <col style={{ width: "1.648cm" }} />
                  <col style={{ width: "1.648cm" }} />
                  <col style={{ width: "1.873cm" }} />
                  <col style={{ width: "2.110cm" }} />
                  <col style={{ width: "1.469cm" }} />
                </colgroup>
                <TableBody>
                  <TableRow sx={{ height: "0.75cm", bgcolor: "grey.100" }}>
                    <TableCell rowSpan={2} sx={{ fontWeight: 700 }}>
                      Stage
                    </TableCell>
                    <TableCell rowSpan={2} sx={{ fontWeight: 700 }}>
                      Supplier
                    </TableCell>
                    <TableCell rowSpan={2} sx={{ fontWeight: 700 }}>
                      Feed name
                    </TableCell>
                    <TableCell rowSpan={2} sx={{ fontWeight: 700 }}>
                      Feed type
                    </TableCell>
                    <TableCell colSpan={2} sx={{ fontWeight: 700 }}>
                      Reading
                    </TableCell>
                    <TableCell colSpan={2} sx={{ fontWeight: 700 }}>
                      Consumption
                    </TableCell>
                    <TableCell rowSpan={2} sx={{ fontWeight: 700 }}>
                      Total weight (kg)
                    </TableCell>
                    <TableCell rowSpan={2} sx={{ fontWeight: 700 }}>
                      <TableSortLabel
                        active
                        direction={feedStartOrder}
                        onClick={handleFeedStartSort}
                      >
                        Date
                      </TableSortLabel>
                    </TableCell>
                    <TableCell rowSpan={2} sx={{ fontWeight: 700 }}>
                      <IconButton
                        size="small"
                        onClick={handleAddFeedRow}
                        aria-label="add"
                        sx={{
                          width: 28,
                          height: 28,
                          bgcolor: "grey.100",
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "grey.800",
                          "&:hover": { bgcolor: "grey.200" },
                        }}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ height: "0.75cm", bgcolor: "grey.100" }}>
                    <TableCell sx={{ fontWeight: 700 }}>
                      Bin A
                      <br />
                      (kg)
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>
                      Bin B
                      <br />
                      (kg)
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>
                      Bin A
                      <br />
                      (kg)
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>
                      Bin B
                      <br />
                      (kg)
                    </TableCell>
                  </TableRow>
                  {sortedFeedRows.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={11}
                        align="center"
                        sx={{ py: 2, color: "text.secondary" }}
                      >
                        No data
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedFeedRows.map((row) => (
                      <TableRow key={row.id} sx={{ "& td": { fontSize: "0.95rem" } }}>
                        <TableCell>{feedStageMap[row.stage] || row.stage || "-"}</TableCell>
                        <TableCell>
                          {feedSupplierMap[row.supplier] || row.supplier || "-"}
                        </TableCell>
                        <TableCell>{feedNameMap[row.feedName] || row.feedName || "-"}</TableCell>
                        <TableCell>{feedTypeMap[row.feedType] || row.feedType || "-"}</TableCell>
                        <TableCell>{row.bin1Reading ?? "-"}</TableCell>
                        <TableCell>{row.bin2Reading ?? "-"}</TableCell>
                        <TableCell>{row.calculatedBin1Consumption || "-"}</TableCell>
                        <TableCell>{row.calculatedBin2Consumption || "-"}</TableCell>
                        <TableCell>{row.calculatedTotalConsumption || "-"}</TableCell>
                        <TableCell>{row.startDateDisplay || "-"}</TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 0.75,
                              flexWrap: "wrap",
                            }}
                          >
                            <EditIcon
                              onClick={() => handleEditFeedRow(row)}
                              sx={{ fontSize: 28, color: "text.secondary", cursor: "pointer" }}
                            />
                            <DeleteIcon
                              onClick={() => handleRemoveFeedRow(row.id)}
                              sx={{ fontSize: 28, color: "text.secondary", cursor: "pointer" }}
                            />
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {feedFloatingScrollbar.visible && (
              <Box
                ref={feedFloatingScrollbarRef}
                onScroll={handleFeedFloatingScrollbarScroll}
                sx={{
                  position: "fixed",
                  left: feedFloatingScrollbar.left,
                  bottom: 8,
                  width: feedFloatingScrollbar.width,
                  height: 18,
                  overflowX: "auto",
                  overflowY: "hidden",
                  bgcolor: "background.paper",
                  boxShadow: 3,
                  zIndex: 1400,
                }}
              >
                <Box sx={{ width: feedFloatingScrollbar.contentWidth, height: 1 }} />
              </Box>
            )}
          </Box>
        </DialogContent>
      </FeedContainer>

      <FeedDetailsDialog
        open={feedDialogOpen}
        onClose={() => {
          if (!feedDialogSaving) closeFeedDialog();
        }}
        onSave={handleFeedDialogSave}
        saving={feedDialogSaving}
        error={feedDialogError}
        stageOptions={feedStageOptions}
        supplierOptions={feedSupplierOptions}
        feedNameOptions={feedNameOptions}
        feedTypeOptions={feedTypeOptions}
        initial={feedDialogInitial}
      />
    </>
  );
}

function FeedDetailsDialog({
  open,
  onClose,
  onSave,
  saving,
  error,
  stageOptions,
  supplierOptions,
  feedNameOptions,
  feedTypeOptions,
  initial,
}) {
  const defaultForm = {
    id: null,
    stage: "",
    supplier: "",
    feedName: "",
    feedType: "",
    weightKg: "",
    bin1: "",
    bin2: "",
    bin1Reading: "",
    bin2Reading: "",
    feedSpendId: null,
    feedPaper: "",
    startDate: "",
    endDate: "",
  };
  const [form, setForm] = useState(defaultForm);

  useEffect(() => {
    if (open) {
      setForm({ ...defaultForm, ...(initial || {}) });
    } else {
      setForm(defaultForm);
    }
  }, [open, initial]);

  const updateField = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const isValidReading = (value) =>
    value === "" || (Number.isInteger(Number(value)) && Number(value) >= 0);
  const canSave =
    !saving &&
    form.stage &&
    form.supplier &&
    form.feedName &&
    form.startDate &&
    isValidReading(form.bin1Reading) &&
    isValidReading(form.bin2Reading);

  const handleSubmit = () => {
    if (!canSave || saving) return;
    onSave?.(form);
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth={false}
      PaperProps={{
        sx: {
          width: "min(540px, calc(100vw - 48px))",
        },
      }}
    >
      <DialogTitle>{form.id ? "Edit Feed Record" : "Add Feed Record"}</DialogTitle>
      <DialogContent dividers>
        <TableContainer component={Box} sx={{ mt: 0.5, boxShadow: "none", overflowX: "hidden" }}>
          <Table
            size="small"
            sx={{
              tableLayout: "fixed",
              minWidth: "100%",
              borderCollapse: "collapse",
              "& th, & td": {
                border: 0,
                fontSize: "1rem",
                lineHeight: 1.2,
                p: 1.25,
              },
              "& th": {
                bgcolor: "grey.100",
                fontWeight: 600,
              },
            }}
          >
            <colgroup>
              <col style={{ width: "50%" }} />
              <col style={{ width: "50%" }} />
            </colgroup>
            <TableBody>
              <TableRow>
                <TableCell sx={{ p: 1.25 }}>
                  <TextField
                    select
                    label="Stage"
                    value={form.stage}
                    onChange={updateField("stage")}
                    fullWidth
                    size="small"
                    InputLabelProps={{ sx: { fontSize: "1rem" } }}
                    SelectProps={{ displayEmpty: true }}
                    sx={{
                      "& .MuiOutlinedInput-root": { minHeight: "1cm" },
                      "& .MuiSelect-select": {
                        minHeight: "1cm",
                        display: "flex",
                        alignItems: "center",
                        fontSize: "1rem",
                      },
                    }}
                  >
                    {stageOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </TableCell>
                <TableCell sx={{ p: 1.25 }}>
                  <TextField
                    select
                    label="Supplier"
                    value={form.supplier}
                    onChange={updateField("supplier")}
                    fullWidth
                    size="small"
                    InputLabelProps={{ sx: { fontSize: "1rem" } }}
                    SelectProps={{ displayEmpty: true }}
                    sx={{
                      "& .MuiOutlinedInput-root": { minHeight: "1cm" },
                      "& .MuiSelect-select": {
                        minHeight: "1cm",
                        display: "flex",
                        alignItems: "center",
                        fontSize: "1rem",
                      },
                    }}
                  >
                    {supplierOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ p: 1.25 }}>
                  <TextField
                    select
                    label="Feed name"
                    value={form.feedName}
                    onChange={updateField("feedName")}
                    fullWidth
                    size="small"
                    InputLabelProps={{ sx: { fontSize: "1rem" } }}
                    SelectProps={{ displayEmpty: true }}
                    sx={{
                      "& .MuiOutlinedInput-root": { minHeight: "1cm" },
                      "& .MuiSelect-select": {
                        minHeight: "1cm",
                        display: "flex",
                        alignItems: "center",
                        fontSize: "1rem",
                      },
                    }}
                  >
                    {feedNameOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </TableCell>
                <TableCell sx={{ p: 1.25 }}>
                  <TextField
                    select
                    label="Feed type"
                    value={form.feedType}
                    onChange={updateField("feedType")}
                    fullWidth
                    size="small"
                    InputLabelProps={{ sx: { fontSize: "1rem" } }}
                    SelectProps={{ displayEmpty: true }}
                    sx={{
                      "& .MuiOutlinedInput-root": { minHeight: "1cm" },
                      "& .MuiSelect-select": {
                        minHeight: "1cm",
                        display: "flex",
                        alignItems: "center",
                        fontSize: "1rem",
                      },
                    }}
                  >
                    {feedTypeOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={2} sx={{ p: 1.25 }}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                      gap: 1.25,
                    }}
                  >
                    <TextField
                      label="Bin A Reading (kg)"
                      value={form.bin1Reading}
                      onChange={updateField("bin1Reading")}
                      fullWidth
                      size="small"
                      InputLabelProps={{ sx: { fontSize: "1rem" } }}
                      InputProps={{ sx: { fontSize: "1rem" } }}
                      type="number"
                      inputProps={{ step: 1, min: 0 }}
                      error={!isValidReading(form.bin1Reading)}
                      helperText={
                        isValidReading(form.bin1Reading) ? "" : "Enter a non-negative integer"
                      }
                      sx={{
                        "& .MuiOutlinedInput-root": { minHeight: "1cm" },
                        "& .MuiInputBase-input": { fontSize: "1rem" },
                      }}
                    />
                    <TextField
                      label="Bin B Reading (kg)"
                      value={form.bin2Reading}
                      onChange={updateField("bin2Reading")}
                      fullWidth
                      size="small"
                      InputLabelProps={{ sx: { fontSize: "1rem" } }}
                      InputProps={{ sx: { fontSize: "1rem" } }}
                      type="number"
                      inputProps={{ step: 1, min: 0 }}
                      error={!isValidReading(form.bin2Reading)}
                      helperText={
                        isValidReading(form.bin2Reading) ? "" : "Enter a non-negative integer"
                      }
                      sx={{
                        "& .MuiOutlinedInput-root": { minHeight: "1cm" },
                        "& .MuiInputBase-input": { fontSize: "1rem" },
                      }}
                    />
                  </Box>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ p: 1.25 }}>
                  <TextField
                    label="Date"
                    type="date"
                    value={form.startDate}
                    onChange={updateField("startDate")}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true, sx: { fontSize: "1rem" } }}
                    InputProps={{ sx: { fontSize: "1rem" } }}
                    sx={{
                      "& .MuiOutlinedInput-root": { minHeight: "1cm" },
                      "& .MuiInputBase-input": { fontSize: "1rem" },
                    }}
                  />
                </TableCell>
                <TableCell sx={{ p: 1.25 }} />
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        {error && (
          <Typography color="error" variant="body2" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!canSave}
          sx={{ color: "#fff" }}
        >
          {saving ? <CircularProgress size={16} /> : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

FeedDetailsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  saving: PropTypes.bool.isRequired,
  error: PropTypes.string,
  stageOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
    })
  ).isRequired,
  supplierOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
    })
  ).isRequired,
  feedNameOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
    })
  ).isRequired,
  feedTypeOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
    })
  ).isRequired,
  initial: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    stage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    supplier: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    feedName: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    feedType: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    weightKg: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    bin1: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    bin2: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    bin1Reading: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    bin2Reading: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    feedSpendId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    feedPaper: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    startDate: PropTypes.string,
    endDate: PropTypes.string,
  }),
};

FeedDetailsDialog.defaultProps = {
  error: null,
  initial: null,
};

SetupCycle.propTypes = {
  facilityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  cycleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  farmId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  embeddedFeed: PropTypes.bool,
  onFeedSaved: PropTypes.func,
};

SetupCycle.defaultProps = {
  onFeedSaved: undefined,
};
